// History panel — renders the past-entries stack from EditorState.history.
//
// Photoshop's History panel:
//   - One row per executed command, top of list = oldest, bottom = newest
//   - Click a row → step doc state back to that point
//   - Snapshot button at top creates a named restore point
//   - Step Forward / Step Backward buttons at the bottom
//
// Previously this was inline HTML in main.ts that only re-rendered when the
// History tab was clicked, so users saw "No history" forever even after
// painting strokes. Closes #152.
import type { EditorState } from "../state/EditorState";
import type { Command } from "../../core/commands/Command";
import type { Snapshot } from "../../core/commands/History";

export interface HistoryPanelDeps {
  state: EditorState;
  /**
   * i10: called whenever the History Brush / Art History Brush source is
   * (re)assigned — either by clicking the source gutter icon on a row, or
   * by the default-on-load assignment (see `ensureHistorySourceDefault`).
   * main.ts wires this to `setHistorySource`, which populates
   * historyBrushTool.sourcePixels / artHistoryBrushTool.sourcePixels.
   */
  onSetSource?: (stateIndex: number) => void;
}

/**
 * Re-exported for backward-compatible naming — a History panel snapshot IS
 * the same `Snapshot` record produced by `History.takeSnapshot()` (i42: the
 * panel no longer keeps its own ad-hoc `{id, name, stateIndex}` bookkeeping;
 * snapshots live on the document's own `History` instance so create/jump
 * work correctly regardless of the current undo/redo stack position).
 */
export type HistorySnapshot = Snapshot;

let _deps: HistoryPanelDeps | null = null;
let _unsub: (() => void) | null = null;
// i10: which history-state index is the History Brush / Art History Brush
// source, per document. Defaults to 0 (the initial/open state) the first
// time a given document is seen — see `ensureHistorySourceDefault`.
const _sourceIndexByDocName = new Map<string, number>();
let _onSnapshotPromptFn: () => string | null = () => {
  return typeof prompt === "function" ? prompt("Snapshot name:", "") : null;
};

export function __resetHistoryPanelForTest(): void {
  if (_unsub) _unsub();
  _unsub = null;
  _deps = null;
  _sourceIndexByDocName.clear();
  _onSnapshotPromptFn = () => (typeof prompt === "function" ? prompt("Snapshot name:", "") : null);
}

/**
 * Read the current History Brush source state-index for a document, or
 * `null` if none has been assigned yet (test helper; also used by the
 * Window > History menu re-sync path).
 */
export function getHistorySourceIndexForTest(docName: string): number | null {
  return _sourceIndexByDocName.get(docName) ?? null;
}

/**
 * i10 — default the History Brush source to the initial/open state (index
 * 0) the first time a document is seen, so the brush works "out of the
 * box" without the user ever opening the History panel or manually picking
 * a source. Cheap (a single Map lookup) and idempotent, so it's safe to
 * call every frame from main.ts's scheduleFrame() as well as from
 * renderHistoryPanel() itself.
 */
export function ensureHistorySourceDefault(deps: HistoryPanelDeps): void {
  const doc = deps.state.doc;
  if (!doc) return;
  if (_sourceIndexByDocName.has(doc.name)) return;
  _sourceIndexByDocName.set(doc.name, 0);
  deps.onSetSource?.(0);
}

/**
 * Override the snapshot-name prompt for tests (default uses window.prompt).
 */
export function setSnapshotPromptForTest(fn: () => string | null): void {
  _onSnapshotPromptFn = fn;
}

/**
 * Get the human-readable label for a command. Falls back to the kind in
 * Title Case if no explicit label is provided. This is what shows in each
 * History panel row.
 */
export function commandDisplayName(command: Command): string {
  // Prefer command.displayName / command.label if a command exposes one.
  const c = command as Command & { displayName?: string; label?: string };
  if (typeof c.displayName === "string" && c.displayName.length > 0) return c.displayName;
  if (typeof c.label === "string" && c.label.length > 0) return c.label;
  return prettifyKind(command.kind);
}

function prettifyKind(kind: string): string {
  // strip "/noop", "/restore" suffixes and Title-Case the kebab name.
  const base = kind.split("/")[0] ?? kind;
  return base
    .split("-")
    .map((w) => (w.length === 0 ? w : w[0]!.toUpperCase() + w.slice(1)))
    .join(" ");
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] ?? c));
}

/** Mount/render the History panel. Subscribes to state changes once. */
export function renderHistoryPanel(deps: HistoryPanelDeps): void {
  _deps = deps;
  ensureHistorySourceDefault(deps);
  if (!_unsub) {
    _unsub = deps.state.subscribe(() => {
      if (_deps) renderInternal(_deps);
    });
  }
  renderInternal(deps);
}

function renderInternal(deps: HistoryPanelDeps): void {
  const root = document.querySelector<HTMLElement>("#history-list");
  if (!root) return;
  const doc = deps.state.doc;
  if (!doc) {
    root.innerHTML = '<div class="muted" style="padding:12px;text-align:center">No document</div>';
    return;
  }
  const entries = deps.state.history.getPastEntries();
  const futureCount = deps.state.history.getFutureEntries().length;
  const currentIndex = entries.length;

  // i42: snapshots live on the document's own History instance (via
  // History.takeSnapshot/getSnapshots) rather than a parallel module-level
  // map keyed by doc name — this is inherently per-document already since
  // `deps.state.history` resolves to the active document's History.
  const snapshots = deps.state.history.getSnapshots();

  const parts: string[] = [];

  // Header toolbar (Snapshot button)
  parts.push(`
    <div class="history-header">
      <button class="history-snapshot-btn ibtn" title="Create new snapshot"
        aria-label="Create new snapshot" data-action="create-snapshot">
        <svg class="ico-xs" aria-hidden="true"><use href="#i-camera"/></svg>
        <span>Snapshot</span>
      </button>
    </div>
  `);

  // Snapshot section (if any)
  if (snapshots.length > 0) {
    parts.push('<div class="history-section-header">Snapshots</div>');
    for (const snap of snapshots) {
      parts.push(`
        <div class="history-row history-row--snapshot" data-snapshot-id="${escapeHtml(snap.id)}">
          <svg class="ico-xs"><use href="#i-camera"/></svg>
          <span class="history-row__name">${escapeHtml(snap.name)}</span>
        </div>
      `);
    }
  }

  // History states section. We always render the initial-state row (the
  // document name) when a doc is loaded — this is what Photoshop does and
  // gives users an explicit "snapshot all the way back" anchor.
  parts.push('<div class="history-section-header">History States</div>');
  // i10: the state currently marked as the History Brush / Art History
  // Brush source — rendered as a clickable icon in the row's left gutter,
  // Photoshop-style. Defaults to 0 via ensureHistorySourceDefault().
  const sourceIndex = _sourceIndexByDocName.get(doc.name) ?? 0;
  const sourceGutter = (idx: number): string => `
      <button type="button" class="history-row__source${idx === sourceIndex ? " is-source" : ""}"
        data-source-index="${idx}" title="Set as History Brush source" aria-label="Set as History Brush source">
        <svg class="ico-xs" aria-hidden="true"><use href="#i-brush"/></svg>
      </button>`;
  // Initial state row (state index 0 — represents pre-command document)
  parts.push(`
    <div class="history-row history-row--initial${currentIndex === 0 ? " is-current" : ""}"
         data-state-index="0">
      ${sourceGutter(0)}
      <span class="history-row__name">${escapeHtml(doc.name)}</span>
    </div>
  `);
  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i]!;
    const stateIdx = i + 1;
    const isCurrent = stateIdx === currentIndex;
    const label = commandDisplayName(entry.command);
    parts.push(`
      <div class="history-row${isCurrent ? " is-current" : ""}"
           data-state-index="${stateIdx}">
        ${sourceGutter(stateIdx)}
        <span class="history-row__name">${escapeHtml(label)}</span>
      </div>
    `);
  }
  // Future entries (greyed out — redoable). Past entries are LIFO; future
  // entries' last index is the next redo. Render them so the next-redo
  // appears at the top of the future block (chronological).
  const futureEntries = deps.state.history.getFutureEntries();
  for (let i = futureEntries.length - 1; i >= 0; i--) {
    const entry = futureEntries[i]!;
    const label = commandDisplayName(entry.command);
    parts.push(`
      <div class="history-row history-row--future"
           data-future-index="${i}">
        <span class="history-row__name">${escapeHtml(label)}</span>
      </div>
    `);
  }
  if (entries.length === 0 && futureCount === 0) {
    // Soft footer note (purely visual — the initial-state row is still
    // present, so the panel is no longer empty per #152).
    parts.push('<div class="muted" style="padding:6px 8px;font-size:11px">No edits yet — start painting to add states.</div>');
  }

  // Footer (step backward / step forward / trash)
  // aria-label mirrors title for screen readers (#47)
  parts.push(`
    <div class="history-footer">
      <button class="ibtn" title="Step backward (Alt+Ctrl+Z)" aria-label="Step backward"
        data-action="step-backward" ${entries.length === 0 ? "disabled" : ""}>
        <svg class="ico-xs" aria-hidden="true"><use href="#i-arrow-left"/></svg>
      </button>
      <button class="ibtn" title="Step forward (Shift+Ctrl+Z)" aria-label="Step forward"
        data-action="step-forward" ${futureCount === 0 ? "disabled" : ""}>
        <svg class="ico-xs" aria-hidden="true"><use href="#i-arrow-right"/></svg>
      </button>
      <button class="ibtn" title="Delete current state" aria-label="Delete current state"
        data-action="delete-state" ${entries.length === 0 ? "disabled" : ""}>
        <svg class="ico-xs" aria-hidden="true"><use href="#i-trash"/></svg>
      </button>
    </div>
  `);

  root.innerHTML = parts.join("");

  wireHandlers(root, deps);
}

function wireHandlers(root: HTMLElement, deps: HistoryPanelDeps): void {
  // Click a history-state row → jump to that state.
  root.querySelectorAll<HTMLElement>(".history-row[data-state-index]").forEach((row) => {
    row.addEventListener("click", () => {
      const targetIdx = parseInt(row.getAttribute("data-state-index") ?? "0", 10);
      jumpToStateIndex(deps, targetIdx);
    });
  });

  // i10: click the gutter icon → mark that state as the History Brush /
  // Art History Brush source (Photoshop-style). stopPropagation so this
  // doesn't also trigger the row's own "jump to state" click handler above.
  root.querySelectorAll<HTMLElement>(".history-row__source").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      const doc = deps.state.doc;
      if (!doc) return;
      const idx = parseInt(btn.getAttribute("data-source-index") ?? "0", 10);
      _sourceIndexByDocName.set(doc.name, idx);
      deps.onSetSource?.(idx);
      renderInternal(deps);
    });
  });

  // Click a future row → step forward enough times to reach it.
  root.querySelectorAll<HTMLElement>(".history-row--future").forEach((row) => {
    row.addEventListener("click", () => {
      const fIdx = parseInt(row.getAttribute("data-future-index") ?? "0", 10);
      // future-index counts from top of stack; need (fIdx + 1) forwards.
      const stepsToTake = fIdx + 1;
      for (let i = 0; i < stepsToTake; i++) deps.state.stepForward();
    });
  });

  // Snapshot rows — i42: jump directly via History.jumpToSnapshot(), which
  // returns the exact Document captured at snapshot time, then hand it to
  // EditorState.setDocument() to make it live. This is correct regardless
  // of where the undo/redo stack currently sits (unlike the old stepForward/
  // stepBackward-based `jumpToStateIndex`, which only worked relative to the
  // in-stack position and broke once new commands invalidated the stack).
  root.querySelectorAll<HTMLElement>(".history-row--snapshot").forEach((row) => {
    row.addEventListener("click", () => {
      const id = row.getAttribute("data-snapshot-id");
      if (!id) return;
      const snaps = deps.state.history.getSnapshots();
      const snap = snaps.find((s) => s.id === id);
      if (!snap) return;
      const restoredDoc = deps.state.history.jumpToSnapshot(snap);
      deps.state.setDocument(restoredDoc);
    });
  });

  // Snapshot button
  root.querySelector<HTMLElement>('[data-action="create-snapshot"]')?.addEventListener("click", () => {
    createSnapshot(deps);
  });

  // Step backward
  root.querySelector<HTMLElement>('[data-action="step-backward"]')?.addEventListener("click", () => {
    deps.state.stepBackward();
  });
  // Step forward
  root.querySelector<HTMLElement>('[data-action="step-forward"]')?.addEventListener("click", () => {
    deps.state.stepForward();
  });
  // Delete current state = undo
  root.querySelector<HTMLElement>('[data-action="delete-state"]')?.addEventListener("click", () => {
    deps.state.undo();
  });
}

function jumpToStateIndex(deps: HistoryPanelDeps, targetIdx: number): void {
  const current = deps.state.history.getPastEntries().length;
  const diff = targetIdx - current;
  if (diff > 0) {
    for (let i = 0; i < diff; i++) deps.state.stepForward();
  } else if (diff < 0) {
    for (let i = 0; i < -diff; i++) deps.state.stepBackward();
  }
}

function createSnapshot(deps: HistoryPanelDeps): Snapshot | null {
  const doc = deps.state.doc;
  if (!doc) return null;
  // i42: delegate to History.takeSnapshot(doc, name) — it stores the
  // snapshot (including the live Document, not just a stack index) on the
  // document's own History instance, so it survives rename/undo/redo and
  // can be jumped to directly later via jumpToSnapshot().
  const existing = deps.state.history.getSnapshots();
  const suggested = `Snapshot ${existing.length + 1}`;
  const promptResult = _onSnapshotPromptFn();
  const name = (promptResult && promptResult.trim().length > 0) ? promptResult.trim() : suggested;
  const snap = deps.state.history.takeSnapshot(doc, name);
  if (_deps) renderInternal(_deps);
  return snap;
}

/**
 * Public helper exposed for the Window menu / keyboard shortcut wiring.
 */
export function createHistorySnapshot(deps: HistoryPanelDeps): Snapshot | null {
  return createSnapshot(deps);
}

/**
 * Test helper: read the snapshots stored on a state's active document
 * History (i42 — snapshots now live on History itself, not a module-level
 * map keyed by doc name).
 */
export function getSnapshotsForTest(state: EditorState): readonly Snapshot[] {
  return state.history.getSnapshots();
}
